'use client';

import {
  Popover as ChakraPopover,
  PopoverProps as ChakraPopoverProps,
  PopoverContent,
  PopoverTrigger,
  PopoverBody,
  PopoverArrow,
  createMultiStyleConfigHelpers
} from '@chakra-ui/react';
import { popoverAnatomy } from '@chakra-ui/anatomy';

const { definePartsStyle, defineMultiStyleConfig } = createMultiStyleConfigHelpers(popoverAnatomy.keys);

const baseStyle = definePartsStyle({
  content: {
    borderRadius: '5px'
  },
  body: {
    p: 4,
    fontSize: '1.125rem'
  }
});

const primaryVariant = definePartsStyle({
  content: {
    color: 'text/reverse',
    borderColor: 'background/dark',
    boxShadow: 'none'
  },
  body: {
    bg: 'background/dark'
  }
});

const secondaryVariant = definePartsStyle({
  content: {
    color: 'text/body',
    borderColor: 'background/secondary',
    boxShadow: 'card.popover'
  },
  body: {
    bg: 'background/secondary'
  }
});

export const popoverTheme = defineMultiStyleConfig({
  baseStyle,
  variants: {
    primary: primaryVariant,
    secondary: secondaryVariant
  },
  defaultProps: {
    variant: 'primary'
  }
});

interface PopoverProps extends ChakraPopoverProps {
  variant?: 'primary' | 'secondary';
  content: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Based on `<Popover>` from ChakraUI component library
 *
 * https://chakra-ui.com/docs/components/popover
 */
const Popover = ({ variant = 'primary', content, children, ...rest }: PopoverProps) => {
  return (
    <ChakraPopover variant={variant} {...rest} arrowSize={17} gutter={13}>
      <PopoverTrigger>{children}</PopoverTrigger>
      <PopoverContent width="fit-content">
        <PopoverArrow
          shadow={variant === 'primary' ? 'none' : 'card.popover'}
          bgColor={variant === 'primary' ? 'background/dark' : 'background/secondary'}
        />
        <PopoverBody maxWidth="360px">{content}</PopoverBody>
      </PopoverContent>
    </ChakraPopover>
  );
};

export default Popover;
